import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tripsApi, type DestinationInput, type NewTraveller } from '../../../api/trips.api';
import { saveImportedDocuments } from '../../../api/importPersist';
import { tipsApi } from '../../../api/tips.api';
import type { Trip } from '../../../types';
import { useTripAutoSuggest, type AiPrompts } from '../../../hooks/useTripAutoSuggest';
import { currentLocale, SUPPORTED_LOCALES, type SupportedLocale } from '../../../i18n';
import { toast } from '../../ui/Toast';
import { destinationOf, wantsAi, type Pace, type WizardForm, type WizardMode } from './types';
import type { DocumentImport } from './useDocumentImport';
import { ticketCount } from '../../../api/ticketImport';

const PACE_MULTIPLIER: Record<Pace, number> = { relaxed: 1.5, balanced: 2, packed: 3 };

function targetVenueCount(nights: number, pace: Pace): number {
  return Math.min(18, Math.max(6, Math.round(nights * PACE_MULTIPLIER[pace])));
}

/**
 * The travellers as the server takes them; empty fields are left out. Everyone
 * but the creator — or, for an agency (ADR-0033), everyone: the first is the
 * lead traveller who will own the trip, and the employee is not in it.
 */
function travellersPayload(form: WizardForm, mode: WizardMode, fallbackName: (n: number) => string): NewTraveller[] {
  const skip = mode.kind === 'agency' ? 0 : 1;
  return form.travellers.slice(skip).map((tr, i) => ({
    name: tr.name.trim() || fallbackName(i + skip + 1),
    ...(tr.birthDate ? { birthDate: tr.birthDate } : {}),
    ...(tr.email.trim() ? { email: tr.email.trim().toLowerCase() } : {}),
    ...(tr.clientId ? { clientId: tr.clientId } : {}),
  }));
}

/** ADR-0030: the legs as the server takes them; one leg holds every night. */
function destinationsPayload(form: WizardForm): DestinationInput[] {
  if (form.legs.length === 1) {
    const [leg] = form.legs;
    return [{ name: leg.name, latitude: leg.lat ?? null, longitude: leg.lng ?? null, nights: form.nights }];
  }
  return form.legs.map(l => ({ name: l.name, latitude: l.lat ?? null, longitude: l.lng ?? null, nights: l.nights }));
}

export function useCreateTrip(form: WizardForm, docs: DocumentImport, mode: WizardMode, onCreated: (trip: Trip) => void) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [createdTrip, setCreatedTrip] = useState<Trip | null>(null);
  const generation = useTripAutoSuggest();

  function aiPrompts(): AiPrompts {
    const k = 'dashboard.newTripWizard';
    const pace = t(`${k}.pace.${form.pace}`);
    const destination = destinationOf(form);
    // Several legs: the prompt says how long in each, so the places follow the nights.
    const destinationForPrompt = form.legs.length > 1
      ? form.legs.map(l => t(`${k}.legForPrompt`, { name: l.name, count: l.nights })).join(', ')
      : destination;
    return {
      placesPrompt: t(`${k}.placesSeedPrompt`, {
        destination: destinationForPrompt,
        nights: form.nights,
        pace,
        // Ages travel in the trip's travellers list, which the server gives the
        // AI as ages (ADR-0029); the prompt only says how many.
        travelers: t(`${k}.groupForPrompt`, { count: form.travellers.length }),
        budget: t(`${k}.budget.${form.budget}`),
        interests: form.selectedInterests.length > 0 ? form.selectedInterests.join(', ') : t(`${k}.noSpecificInterests`),
        notes: form.notes.trim(),
        targetCount: targetVenueCount(form.nights, form.pace),
      }).trim(),
      itineraryPrompt: t(`${k}.itinerarySeedPrompt`, { pace }).trim(),
      tipsPrompt: t(`${k}.tipsSeedPrompt`, { destination }).trim(),
    };
  }

  async function submit() {
    if (!form.name.trim() || loading) return;
    setLoading(true);
    // A trip built from a document speaks that document's language: its
    // itinerary, notes and tips all come from there. Tickets and confirmations
    // detect as null and leave the UI locale in place.
    const contentLanguage = (docs.language && SUPPORTED_LOCALES.includes(docs.language as SupportedLocale))
      ? docs.language as SupportedLocale
      : currentLocale();
    const agency = mode.kind === 'agency' ? mode : null;
    // Your own date goes to your profile; an agency's employee is not travelling.
    const ownerBirthDate = agency ? undefined : form.travellers[0]?.birthDate;
    try {
      const { invitesFailed, ...trip } = await tripsApi.create({
        name: form.name.trim(),
        destination: destinationOf(form),
        destinationLat: form.legs[0]?.lat,
        destinationLng: form.legs[0]?.lng,
        destinations: destinationsPayload(form),
        startDate: form.startDate,
        endDate: form.endDate,
        nights: form.nights,
        notes: form.notes.trim(),
        budgetCurrency: form.budgetCurrency,
        contentLanguage,
        travellers: travellersPayload(form, mode, n => t('dashboard.newTripWizard.travellerDefaultName', { number: n })),
        ...(ownerBirthDate ? { ownerBirthDate } : {}),
        ...(agency ? { agencyId: agency.agencyId, isTemplate: agency.isTemplate } : {}),
      });
      toast(t('dashboard.newTripWizard.created'), 'success');
      if (invitesFailed > 0) toast(t('dashboard.newTripWizard.invitesFailed', { count: invitesFailed }), 'error');

      // Free scaffolding (no AI cost), in the trip's language: these titles
      // are trip content shared by everyone on it. Each member's own suitcase,
      // so not for agency staff, who have none (ADR-0033).
      if (!agency) {
        tipsApi.seedDefaultSections(trip.id, t('packing.defaultSectionNames', { returnObjects: true, lng: contentLanguage }) as string[])
          .catch(() => {});
      }

      // The originals uploaded during creation are kept in the Notes tab.
      if (docs.staged.length > 0) {
        saveImportedDocuments(trip.id, docs.staged, t('import.savedDocsNote')).catch(() => {});
      }

      // Complementary: tickets, AI suggestions and the documents' content, any of them.
      const ai = wantsAi(form);
      const withTickets = ticketCount(form.tickets) > 0;
      if (ai || docs.plan || withTickets) {
        setCreatedTrip(trip);
        generation.generateAll({
          tripId: trip.id,
          aiPrompts: ai ? aiPrompts() : undefined,
          importPlan: docs.plan ?? undefined,
          tickets: withTickets ? { files: form.tickets, notesTitle: t('import.savedDocsNote') } : undefined,
        });
      } else {
        onCreated(trip);
      }
    } catch {
      toast(t('dashboard.newTripWizard.failed'), 'error');
    } finally {
      setLoading(false);
    }
  }

  return { submit, loading, createdTrip, generation };
}
